import { Fuel, Nullable, VehicleModelType } from '@orcar/common';
import {
  convertToKST,
  FAMILY,
  filterInsuranceAtom,
  filterOptionAtom,
  getPrimaryColor,
  getSureUserId,
  HomePopup,
  isMobileAtom,
  MODAL_TYPE,
  openSiteLink,
  PAGE_NAME,
  SearchIcon,
  SearchParamsType,
  setStaticDate,
  siteImageUrl,
  splitBoldMarks,
  useGetVehicleModels,
  useRecoil,
} from '@orcar/common-d2c';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KakaoChannelButton } from '@/components/button/KakaoChannelButton';
import { HOME_HERO_FALLBACK } from '@/constants/site-content';
import {
  useHomeBanners,
  useHomeHero,
  useHomePopup,
} from '@/hooks/useHomeImages';
import { useSearchOptions } from '@/hooks/useSearchOptions';
import { useContactProfile } from '@/hooks/useSiteProfile';
import {
  trackBannerClicked,
  trackHomepageViewed,
  trackSearchStarted,
} from '@/utils/analytics';
import RecommendSection from './components/recommendedCar/RecommendSection';
import { MobileOptionSearchModal } from './components/searchModal/MobileOptionSearchModal';
import { SearchModal } from './components/searchModal/SearchModal';
import * as S from './styles';

// ERP 문구에서 **로 감싼 부분을 굵게 보여 준다.
const renderHeadline = (text = '') =>
  splitBoldMarks(text).map((part, index) =>
    part.bold ? (
      <S.Text key={index} bold>
        {part.text}
      </S.Text>
    ) : (
      <Fragment key={index}>{part.text}</Fragment>
    ),
  );

const HomeLayout = () => {
  const navigate = useNavigate();
  const { kakaoChannelUrl } = useContactProfile();
  const { list: heroList, items: heroItems } = useHomeHero();
  const { list: bannerList, items: bannerItems } = useHomeBanners();
  const { items: popupItems } = useHomePopup();
  const hero = heroItems[0];
  // 문구는 받기 전에도 같은 자리를 차지하게 두고 보이지만 않게 한다.
  const heroText = heroList ?? HOME_HERO_FALLBACK;
  const banner = bannerItems[0];
  const sureUserId = getSureUserId();

  const [isModalOpen, setIsModalOpen] = useState<Nullable<string>>(null);

  const { recoilValue: isMobile } = useRecoil(isMobileAtom);
  const {
    recoilState: [recoilFilterOption, setRecoilFilterOption],
    resetRecoilState: resetFilterOption,
  } = useRecoil(filterOptionAtom);
  const { resetRecoilState: resetFilterInsurance } =
    useRecoil(filterInsuranceAtom);

  const { data: vehicleModelList } = useGetVehicleModels({
    params: {
      pickUpAt: convertToKST(setStaticDate('pickUp')),
      dropOffAt: convertToKST(setStaticDate('dropOff')),
      driverAge: undefined,
      numberOfPeople: undefined,
      drivingExperience: undefined,
      sureUserId: sureUserId ? Number(sureUserId) : undefined,
    },
  });

  const handleSubmitSearch = (params?: SearchParamsType) => {
    trackSearchStarted({
      pickUpDate: params?.pickUpAt,
      dropOffDate: params?.dropOffAt,
    });

    navigate({
      pathname: PAGE_NAME.search,
      search: new URLSearchParams({
        pickUpAt: convertToKST(params?.pickUpAt),
        dropOffAt: convertToKST(params?.dropOffAt),
        driverAge: params?.driverAge || '',
        drivingExperience: params?.drivingExperience || '',
        numberOfPeople: params?.numberOfPeople || '',
      }).toString(),
    });
    if (isMobile) {
      setIsModalOpen(null);
    }
  };

  const handleOpenModal = () => {
    setIsModalOpen(isMobile ? MODAL_TYPE.MOBILE_SEARCH : MODAL_TYPE.WEB_SEARCH);
  };

  const handleCloseModal = () => {
    setIsModalOpen(null);
  };

  const {
    searchOptions,
    searchOptionIdx,
    setSearchOptionIdx,
    isNextBtnDisabled,
    isCheckTimeSelected,
    indicatorTexts,
    handleClickIndicator,
    handleNextButton,
    handleTabButton,
  } = useSearchOptions({
    onSubmit: handleSubmitSearch,
    onModalClose: handleCloseModal,
  });

  const handleRecommendCar = (type: VehicleModelType | Fuel) => {
    const pickUpAt = convertToKST(searchOptions.pickUpDate?.format());
    const dropOffAt = convertToKST(searchOptions.dropOffDate?.format());

    navigate({
      pathname: PAGE_NAME.search,
      search: `?pickUpAt=${pickUpAt}&dropOffAt=${dropOffAt}`,
    });

    if (Fuel.EV === type) {
      setRecoilFilterOption({
        ...recoilFilterOption,
        carFuelList: [Fuel.EV],
        carTypeList: [VehicleModelType.EV],
      });
    } else {
      setRecoilFilterOption({
        ...recoilFilterOption,
        carTypeList: [type],
      });
    }
  };

  useEffect(() => {
    if (location.pathname === '/') {
      resetFilterOption();
      resetFilterInsurance();
      trackHomepageViewed();
    }
  }, [location.pathname]);

  return (
    <S.Wrapper isMobile={isMobile}>
      <HomePopup
        items={popupItems}
        dismissKey='family_home_popup_dismissed_until'
        accentColor={getPrimaryColor(FAMILY)}
      />
      <S.BackgroundImageWrapper isMobile={isMobile}>
        {hero && (
          <img
            src={
              isMobile
                ? siteImageUrl(hero.mobileImage || hero.image, 1080)
                : siteImageUrl(hero.image, 1920)
            }
            width={isMobile ? '100%' : '1200px'}
            height={isMobile ? '100%' : '380px'}
            alt={hero.alt ?? ''}
            style={{
              objectFit: isMobile ? 'cover' : undefined,
            }}
          />
        )}
        <S.BackgroundImageFilter isMobile={isMobile} />
      </S.BackgroundImageWrapper>
      <S.Text
        hidden={isModalOpen === MODAL_TYPE.WEB_SEARCH}
        isMobile={isMobile}
        style={heroList ? undefined : { visibility: 'hidden' }}
      >
        {renderHeadline(heroText.headline)} <br />
        {renderHeadline(heroText.headlineLine2)}
      </S.Text>
      <S.SearchButtonWrapper
        className='homeSearchBar'
        onClick={handleOpenModal}
        isOpen={isModalOpen === MODAL_TYPE.WEB_SEARCH}
        isMobile={isMobile}
      >
        <S.ButtonText>여행 일정을 알려주세요</S.ButtonText>
        <S.ButtonImg
          src={SearchIcon}
          alt='search-icon'
          width={19}
          height={19}
        />
      </S.SearchButtonWrapper>
      <RecommendSection
        vehicleModelList={vehicleModelList}
        onClick={handleRecommendCar}
        isMobile={isMobile}
      />
      {banner && (
        <>
          {bannerList?.sectionTitle && (
            <S.BannerSection isMobile={isMobile}>
              <S.SectionTitle>{bannerList.sectionTitle}</S.SectionTitle>
            </S.BannerSection>
          )}
          <S.BannerWrapper isMobile={isMobile}>
            <S.ImgWrapper>
              <img
                src={
                  isMobile
                    ? siteImageUrl(banner.mobileImage || banner.image, 1080)
                    : siteImageUrl(banner.image, 1920)
                }
                alt={banner.alt ?? ''}
                width={isMobile ? undefined : '1400px'}
                onClick={() => {
                  trackBannerClicked({ banner_name: banner.id });
                  openSiteLink(banner.link, navigate);
                }}
                style={{ cursor: 'pointer' }}
              />
            </S.ImgWrapper>
          </S.BannerWrapper>
        </>
      )}
      <p className=' xs'></p>
      <MobileOptionSearchModal
        showModal={isModalOpen === MODAL_TYPE.MOBILE_SEARCH}
        onClose={handleCloseModal}
        searchOptionIdx={searchOptionIdx}
        setSearchOptionIdx={setSearchOptionIdx}
        searchOptions={searchOptions}
        isNextBtnDisabled={isNextBtnDisabled}
        isCheckTimeSelected={isCheckTimeSelected}
        handleNextBtn={handleNextButton}
        handleTabBtn={handleTabButton}
      />
      <SearchModal
        showModal={isModalOpen === MODAL_TYPE.WEB_SEARCH}
        onClose={() => setIsModalOpen(null)}
        searchOptions={searchOptions}
        handleClickIndicator={handleClickIndicator}
        handleNextButton={handleNextButton}
        searchOptionIdx={searchOptionIdx}
        setSearchOptionIdx={setSearchOptionIdx}
        indicatorTexts={indicatorTexts}
        isNextBtnDisabled={isNextBtnDisabled}
        isCheckTimeSelected={isCheckTimeSelected}
      />
      {kakaoChannelUrl && <KakaoChannelButton linkUrl={kakaoChannelUrl} />}
    </S.Wrapper>
  );
};

export default HomeLayout;
